import type { ReactNode } from "react";
import styled from "styled-components";
import TermsOfServiceBody from "@/shared/components/ToS/TermsOfServiceBody";

const TermsCard = styled.section`
	width: min(720px, 100%);
	height: min(720px, calc(100vh - 48px));
	min-height: 0;
	padding: 28px;
	border-radius: 24px;
	background: #fff;
	box-shadow: 0 20px 55px rgba(0, 57, 127, 0.22);
	display: flex;
	flex-direction: column;
	overflow: hidden;
	@media (max-width: 700px) {
		height: 100vh;
		padding: 16px;
		border-radius: 0;
	}
`;
const TermsPanel = styled.div<{ $fullBleed?: boolean }>`
	--panel-inline-start: 28px;
	min-height: 0;
	flex: 1;
	padding: 28px 0 0 28px;
	border: 1px solid #f1f1f1;
	border-radius: 8px;
	background: ${({ $fullBleed }) => ($fullBleed ? "#f3f5ff" : "#fafafa")};
	display: flex;
	flex-direction: column;
	overflow: hidden;
	@media (max-width: 700px) {
		--panel-inline-start: 20px;
		padding: 24px 20px 0;
	}
`;
const BackButton = styled.button`
	align-self: flex-start;
	margin: 0 0 24px 14px;
	padding: 0;
	border: 0;
	background: transparent;
	display: inline-flex;
	align-items: center;
	gap: 10px;
	color: #303030;
	font: inherit;
	font-size: 0.95rem;
	cursor: pointer;
	&::before {
		content: "";
		width: 9px;
		height: 9px;
		border-left: 2px solid currentColor;
		border-bottom: 2px solid currentColor;
		transform: rotate(45deg);
	}
`;
const TermsTitle = styled.h1`
	margin: 0 0 38px;
	color: #303030;
	font-size: 30px;
	line-height: 30px;
	font-weight: 600;
	letter-spacing: 0;
	@media (max-width: 640px) {
		font-size: 1.6rem;
	}
`;
const TermsScroller = styled.div<{ $fullBleed?: boolean; $overlayScrollbar?: boolean }>`
	min-height: 0;
	flex: 1;
	overflow-y: ${({ $overlayScrollbar }) => ($overlayScrollbar ? "scroll" : "auto")};
	margin-left: ${({ $fullBleed }) => ($fullBleed ? "calc(-1 * var(--panel-inline-start))" : "0")};
	width: ${({ $fullBleed }) => ($fullBleed ? "calc(100% + var(--panel-inline-start))" : "auto")};
	padding: ${({ $fullBleed }) => ($fullBleed ? "0" : "0 28px 28px 0")};
	background: ${({ $fullBleed }) => ($fullBleed ? "#f3f5ff" : "transparent")};
	scrollbar-color: ${({ $overlayScrollbar }) => ($overlayScrollbar ? "#b8b8b8 #f3f5ff" : "auto")};
	scrollbar-gutter: stable;
	${({ $overlayScrollbar }) =>
		$overlayScrollbar
			? `
		&::-webkit-scrollbar {
			width: 10px;
			background: #f3f5ff;
		}
		&::-webkit-scrollbar-track {
			background: #f3f5ff;
		}
		&::-webkit-scrollbar-thumb {
			border: 2px solid #f3f5ff;
			border-radius: 999px;
			background: #b8b8b8;
		}
		&::-webkit-scrollbar-corner {
			background: #f3f5ff;
		}
	`
			: ""}
	@media (max-width: 700px) {
		padding-right: ${({ $fullBleed }) => ($fullBleed ? "0" : "20px")};
	}
	& > div {
		max-height: none;
		overflow: visible;
		padding: 0;
		font-size: 0.9rem;
		line-height: 24px;
	}
`;

type LegalDocumentCardFrameProps = {
	title?: string;
	onBack: () => void;
	children: ReactNode;
	fullBleed?: boolean;
	overlayScrollbar?: boolean;
};

export const LegalDocumentCardFrame = ({
	title,
	onBack,
	children,
	fullBleed,
	overlayScrollbar,
}: LegalDocumentCardFrameProps) => (
	<TermsCard>
		<TermsPanel $fullBleed={fullBleed}>
			<BackButton type="button" onClick={onBack}>
				Back
			</BackButton>
			{title ? <TermsTitle>{title}</TermsTitle> : null}
			<TermsScroller
				$fullBleed={fullBleed}
				$overlayScrollbar={overlayScrollbar}
				data-full-bleed={fullBleed || undefined}
			>
				{children}
			</TermsScroller>
		</TermsPanel>
	</TermsCard>
);

type TermsOfServiceCardProps = { onBack: () => void };

export const TermsOfServiceCard = ({ onBack }: TermsOfServiceCardProps) => (
	<LegalDocumentCardFrame onBack={onBack} fullBleed overlayScrollbar>
		<TermsOfServiceBody fullPage />
	</LegalDocumentCardFrame>
);
